import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useEffectOnce } from "../../../hooks/useEffectOnce";
import useRetryFetch from "../../../hooks/useRetryFetch";

import { config } from "../../../Environment";
import { useState, useContext, useEffect, useMemo, useRef, useCallback } from "react";
import convertPrescription from "../../reactflow/convert_prescription";
import {
  mergeDataEventAnalysisFromLivePrescription,
  applyLivePrescriptionDiagramMerge,
} from "../../../lib/mergeRuntimeDiagram";
import LoggerContext from "../../../context/LoggerProvider";
import { WSContext } from "../../../App";
import useAuth from "../../../hooks/useAuth";
import { LANGUAGES } from "../../../lib/utils";
import { EXPERT_ROLE } from "../../../lib/roles";
import { trackAppEvent } from "../../../lib/appTracking";
import RenderDialog from "../../../components/RenderDialog";
import mockBranding from "../../../branding/mockBranding.json";
import AccessDenied from "../AccessDenied";
import ActivityLoadError from "../ActivityLoadError";
import {
  EngagementTaggingDialog,
  type EngagementTaggingResult,
} from "../modals/EngagementTaggingDialog";

const ActivityRunningPreview = ({
  setActivityLastUpdatedDate,
  setPrescriptionVersionNo,
  setIsActivityCompletedModalOpen,
  processId,
  setProcessId,
  onQuotePane,
  onNavigateToField,
  onAddTakeaway,
  saveProgressRef,
  hasChangesRef,
  activityMetadata,
  onFollowExample,
  currentActivityRef,
  setCurrentActivityForExample,
  onContentScroll,
  mobileActionsSlot,
}: any) => {
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { auth, user } = useAuth();
  const isCurrentUserExpert = Array.isArray(user?.roles) && user.roles.includes(EXPERT_ROLE);

  // Engagement tagging dialog state (for expert users before starting a new process)
  const [showTaggingDialog, setShowTaggingDialog] = useState(false);
  const pendingStartProcessId = useRef<string | null>(null);

  const [diagram, setDiagram] = useState<any>({});
  const [currentActivity, setCurrentActivity] = useState("");
  const [initialActivity, setInitialActivity] = useState("");
  const [initialStepStack, setInitialStepStack] = useState<any>([]);
  const [userData, setUserData] = useState({});
  /** Prescription settings: soft exit when same + resume most recent + landing pane configured */
  const [resumeExitSettings, setResumeExitSettings] = useState<{
    softExit: boolean;
    landingActivityId: string;
  }>({ softExit: false, landingActivityId: "" });

  // Auto-play related state
  const [additionalInfoRecordings, setAdditionalInfoRecordings] = useState<any>(undefined);
  const [autoPlayState, setAutoPlayState] = useState<any>(undefined);

  const [accessDenied, setAccessDenied] = useState<{
    reason: "unauthorized" | "forbidden" | "not_found";
    activityName?: string;
  } | null>(null);
  const [loadError, setLoadError] = useState<{
    errorType: "prescription_not_found" | "process_load_failed" | "network_error" | "general_error";
    errorMessage?: string;
    activityName?: string;
  } | null>(null);
  // const [activityBranding, setActivityBranding] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const { Logger } = useContext(LoggerContext);

  // Track the last saved userData to avoid duplicate saves
  const lastSavedUserDataRef = useRef<string>("");
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if user has progressed to another activity (clicked "Next")
  const hasProgressedRef = useRef<boolean>(false);
  // Track if process has been finished — prevents auto-save from overwriting FINISHED with RUNNING
  const processFinishedRef = useRef<boolean>(false);
  // Always-current snapshots of userData/currentActivity, used to detect staleness
  // in async saves (saveCurrentProgress) that span an await.
  const userDataRef = useRef(userData);
  const localCurrentActivityRef = useRef(currentActivity);
  // const { setListRefresh } = useContext(WSContext);

  // Update ref for parent to access current activity info (for example following)
  useEffect(() => {
    if (currentActivityRef && currentActivity && diagram) {
      currentActivityRef.current = {
        currentActivity,
        diagram,
        prescriptionLanguage: i18n.language,
      };

      // Update state for example pane synchronization
      if (setCurrentActivityForExample) {
        setCurrentActivityForExample(currentActivity);
      }
    }
  }, [currentActivity, diagram, currentActivityRef, i18n.language, setCurrentActivityForExample]);

  /**
   * The `setSearchParams` function from `useSearchParams` causes a navigation,
   * so we use the `replaceState` function from the browser's History API instead.
   * @param patch object with key-value pairs to set or update in the query string
   */
  function updateSearchParams(patch: object) {
    /*
    setSearchParams((current) => {
      Object.entries(patch).forEach(([key, value]) => {
        current.set(key, value);
      });
      return current;
    });
    */
    const browserSearchParams = new URLSearchParams(window.location.search);
    Object.entries(patch).forEach(([key, value]) => {
      browserSearchParams.set(key, value);
    });
    window.history.replaceState({}, "", "?" + browserSearchParams.toString());
    window.dispatchEvent(new Event("urlchange"));
  }

  /*
  const getQueryParams = () => {
    let result: any = {};

    let queryString = window.location.search;
    let keyValues = queryString.substring(1).split("&");

    for (let keyValue of keyValues) {
      let keyAndValue = keyValue.split("=");
      result[keyAndValue[0]] = keyAndValue[1];
    }
    Logger.debug(result);

    return result;
  };
  */

  const getStartActivity = (diagram: any) => {
    let result = "";

    let activityKeys = Object.keys(diagram.data);
    for (let actID of activityKeys) {
      let actData = diagram.data[actID];
      if (actData.type === "start") {
        result = actID;
        break;
      }
    }

    return result;
  };

  function updateBranding(activityInitiatorEmail: string) {
    const matchedBrandKey = Object.keys(mockBranding).find(
      (key) =>
        (mockBranding as Record<string, { email: string }>)[key].email ===
        activityInitiatorEmail
    );
    if (matchedBrandKey) {
      updateSearchParams({ br: matchedBrandKey });
    }
  }
  async function startProcess(
    prescriptionId: string,
    taggingResult?: EngagementTaggingResult,
  ) {
    // Show tagging dialog before starting a new engagement (expert users only)
    if (isCurrentUserExpert && !taggingResult) {
      pendingStartProcessId.current = prescriptionId;
      setShowTaggingDialog(true);
      return;
    }

    try {
      const bodyPayload: any = { email: auth.email };
      if (taggingResult?.tags && taggingResult.tags.length > 0) {
        bodyPayload.tags = taggingResult.tags;
      }
      if (taggingResult?.clientIdentifier) {
        bodyPayload.clientIdentifier = taggingResult.clientIdentifier;
      }
      if (taggingResult?.engagementName) {
        bodyPayload.engagementName = taggingResult.engagementName;
      }

      const response = await retryFetch(
        `${config.API_BASE}/api/processes/${prescriptionId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyPayload),
        }
      );

      // Check if the request was successful
      if (!response.ok) {
        if (response.status === 403) {
          // User doesn't have permission to access this activity
          // Check localStorage for auth state synchronously (more reliable than context)
          let isAuthenticated = false;
          try {
            const authData = localStorage.getItem("auth");
            isAuthenticated = !!(authData && JSON.parse(authData)?.email);
          } catch (e) {
            // If localStorage parse fails, fall back to context
            isAuthenticated = !!auth?.email;
          }

          setAccessDenied({
            reason: isAuthenticated ? "forbidden" : "unauthorized",
            activityName: activityMetadata?.name,
          });
          return;
        } else if (response.status === 404) {
          // Activity not found
          setAccessDenied({
            reason: "not_found",
            activityName: activityMetadata?.name,
          });
          return;
        } else {
          // Other HTTP errors - show error screen
          const errorData = await response.json().catch(() => ({}));
          setLoadError({
            errorType: "process_load_failed",
            errorMessage: errorData?.error || t("failed_to_start_activity") || "Failed to start activity. Please try again.",
            activityName: activityMetadata?.name,
          });
          return;
        }
      }

      let process = await response.json();
      processFinishedRef.current = false;
      setProcessId(process.processId);
      updateSearchParams({ wid: prescriptionId, pid: process.processId });
      // updateBranding(process?.fiExpert?.email)
      console.log(
        `ActivityRunningPreview useEffectOnce set wid ${prescriptionId} pid ${process.processId}`
      );

      trackAppEvent("engagement_started", {
        processId: process.processId,
        prescriptionId,
        activityName: activityMetadata?.name,
      });

      // Fetch prescription to get additionalInfoRecordings for auto-play
      let livePrescriptionDiagramJSON: string | undefined;
      try {
        const presResponse = await retryFetch(
          `${config.API_BASE}/api/prescriptions/${prescriptionId}`
        );
        if (presResponse.ok) {
          const presData = await presResponse.json();
          setAdditionalInfoRecordings(presData.additionalInfoRecordings || {});
          livePrescriptionDiagramJSON = presData.diagramJSON;
        }
      } catch (err) {
        console.warn("Failed to fetch prescription for auto-play:", err);
      }

      // Initialize auto-play state (new process, so empty)
      setAutoPlayState({});

      let dia = convertPrescription(process.diagramJSON);
      if (livePrescriptionDiagramJSON) {
        dia = mergeDataEventAnalysisFromLivePrescription(
          dia,
          livePrescriptionDiagramJSON,
        );
      }
      // Add author information to diagram for proper author detection
      dia.fiUser = process.fiUser;

      const nextAct = getStartActivity(dia);
      setInitialActivity(nextAct);
      setInitialStepStack([]);
      setCurrentActivity(nextAct);

      Logger.debug("startProcess ", process.processId, nextAct);

      setDiagram(dia);

      // Fetch the process with persistent data now that it's created
      const processDetailResponse = await retryFetch(
        `${config.API_BASE}/api/processes/${process.processId}`
      );
      const processDetail = await processDetailResponse.json();

      // Initialize userData with persistent data from last engagement if available
      let initialUserData:any = {};
      if (processDetail.filledSelectedComponents?.selectedComponentsToCopy) {
        Logger.debug("Populating persistent data from last engagement on new process:", processDetail.filledSelectedComponents);
        const selectedComponents = processDetail.filledSelectedComponents.selectedComponentsToCopy;

        // Extract values from the nested structure
        for (const [stepId, components] of Object.entries(selectedComponents)) {
          if (Array.isArray(components)) {
            for (const componentObj of components) {
              if (typeof componentObj === 'object' && componentObj !== null) {
                for (const [compId, value] of Object.entries(componentObj)) {
                  if (value !== null && value !== undefined) {
                    initialUserData[compId] = value;
                    Logger.debug(`Pre-filled component ${compId} with value:`, value);
                  }
                }
              }
            }
          }
        }
      }

      setUserData(initialUserData);
      // Reset progress flag for new process
      hasProgressedRef.current = false;

      // store prescriptionID and processID for design mode
      // TODO: need to check how these are used, might need to replace this with author-only menu item to open in design mode and then pass it to design mode in query parameters; it won't be reading from sessionStorage anymore
      sessionStorage.setItem("prescriptionID", prescriptionId);
      sessionStorage.setItem("runProcessID", process.processId);
      sessionStorage.setItem(
        "prescriptionHistoryID",
        process.prescriptionHistoryID
      );
    } catch (error: any) {
      // Network errors or other exceptions while processing
      Logger.error("Error in startProcess:", error);
      setLoadError({
        errorType: error?.message?.toLowerCase().includes("network") || error?.message?.toLowerCase().includes("fetch")
          ? "network_error"
          : "general_error",
        errorMessage: error?.message || t("failed_to_start_activity") || "Failed to start activity. Please try again.",
        activityName: activityMetadata?.name,
      });
      return;
    }
  }

  async function startProcessFromCache(storedDraftPrescriptionId: string) {
    let dia = convertPrescription(storedDraftPrescriptionId);
    let startAct = getStartActivity(dia);
    setInitialActivity(startAct);
    setCurrentActivity(startAct);
    setInitialStepStack([]);
    setDiagram(dia);
  }

  /** Last meaningful activity id in stack (for resuming FINISHED when nextActivity was cleared). */
  function getLastResumeActivityFromStepStack(
    stepStack: unknown,
    dia: any
  ): string {
    if (!Array.isArray(stepStack) || stepStack.length === 0) {
      return getStartActivity(dia);
    }
    for (let i = stepStack.length - 1; i >= 0; i--) {
      const act = (stepStack[i] as { activity?: string })?.activity;
      if (!act || act.startsWith("CURRENT_SCREEN|")) continue;
      if (act.startsWith("TERMINATE")) continue;
      if (act.startsWith("REOPEN|")) continue;
      return act;
    }
    return getStartActivity(dia);
  }

  async function startProcessFromStoredId(
    storedPrescriptionId: string,
    locProcessId: string | null | undefined
  ) {
    // is it a running process (runMode) or a preview?
    if (locProcessId) {
      // runMode
      Logger.debug(
        "runMode detected for processID of localStorage",
        locProcessId
      );
      setProcessId(locProcessId);
      updateSearchParams({ pid: locProcessId });
      console.log(`useEffectOnce set wid locProcessId ${locProcessId}`);

      try {
        let response = await retryFetch(
          `${config.API_BASE}/api/processes/${locProcessId}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));

          // Handle different error scenarios
          if (response.status === 404) {
            // Process no longer exists (expired, deleted, or invalid ID)
            setAccessDenied({
              reason: "not_found",
              activityName: activityMetadata?.name,
            });
            return;
          } else if (response.status === 403) {
            // User doesn't have permission
            setAccessDenied({
              reason: "forbidden",
              activityName: activityMetadata?.name,
            });
            return;
          } else if (response.status === 401) {
            // User needs to log in
            setAccessDenied({
              reason: "unauthorized",
              activityName: activityMetadata?.name,
            });
            return;
          } else {
            // Other errors (500, network issues, etc.)
            setLoadError({
              errorType: "process_load_failed",
              errorMessage: errorData?.error || t("failed_to_load_engagement") || "Failed to load engagement data.",
              activityName: activityMetadata?.name,
            });
            return;
          }
        }

        let process = await response.json();
        processFinishedRef.current = false;
      // updateBranding(process?.fiExpert?.email)
      Logger.debug(
        `ActivityRunningPreview showPreview loaded process data: ${JSON.stringify(
          process
        )}`
      );

      // Extract auto-play state from process
      setAutoPlayState(process.autoPlayState || {});

      // Fetch prescription to get additionalInfoRecordings for auto-play
      const prescriptionId = process.fiPrescription?._id || process.fiPrescription;
      let livePrescriptionDiagramJSON: string | undefined;
      if (prescriptionId) {
        try {
          const presResponse = await retryFetch(
            `${config.API_BASE}/api/prescriptions/${prescriptionId}`
          );
          if (presResponse.ok) {
            const presData = await presResponse.json();
            setAdditionalInfoRecordings(presData.additionalInfoRecordings || {});
            livePrescriptionDiagramJSON = presData.diagramJSON;
          }
        } catch (err) {
          console.warn("Failed to fetch prescription for auto-play:", err);
        }
      }

      // TODO: fiPrescriptionHistory may be null here , so we might need to load the prescription from the server
      let dia = convertPrescription(process.fiPrescriptionHistory.diagramJSON);
      if (livePrescriptionDiagramJSON) {
        dia = mergeDataEventAnalysisFromLivePrescription(
          dia,
          livePrescriptionDiagramJSON,
        );
      }
      // Add author information to diagram for proper author detection
      dia.fiUser = process.fiPrescriptionHistory.fiUser || process.fiUser;
      if (!dia.prescriptionLanguage && (process.fiPrescriptionHistory as any)?.prescriptionLanguage) {
        dia.prescriptionLanguage = (process.fiPrescriptionHistory as any).prescriptionLanguage;
      }

      setPrescriptionVersionNo(undefined); // TODO: we need the document version here, which we will eventually get from the prescription metadata API; dia.version might be a format version, it's always '2'

      const isTerminalEngagement =
        process.status === "FINISHED" || process.status === "TERMINATED";

      // Only reset FINISHED/TERMINATED to pane 1 + prefill when Activity Settings use
      // "Always use the same engagement" + "Start a new engagement" (resumeFinished !== true).
      // "Always resume the most recent engagement" keeps full resume (last pane + aggregated data).
      let forceRestartFinishedEngagement = false;
      let resumeLandingFromSettings: string | null = null;
      const settingsPrescriptionId =
        storedPrescriptionId ||
        String(process.fiPrescription?._id ?? process.fiPrescription ?? "");
      if (settingsPrescriptionId) {
        try {
          const settingsRes = await retryFetch(
            `${config.API_BASE}/api/prescriptions/${settingsPrescriptionId}/settings`,
            { credentials: "include" }
          );
          if (settingsRes.ok) {
            const settings = await settingsRes.json();
            forceRestartFinishedEngagement =
              settings.engagementMode === "same" &&
              settings.resumeFinished !== true;
            if (
              settings.engagementMode === "same" &&
              settings.resumeFinished === true &&
              typeof settings.resumeLandingActivityId === "string" &&
              settings.resumeLandingActivityId.length > 0
            ) {
              resumeLandingFromSettings = settings.resumeLandingActivityId;
            }
          }
        } catch (err) {
          Logger.debug(
            "startProcessFromStoredId: settings fetch failed; terminal engagements will resume, not force-restart",
            err
          );
        }
      }

      let nextAct = process.nextActivity;
      if (
        isTerminalEngagement &&
        !forceRestartFinishedEngagement &&
        (!nextAct || nextAct === "")
      ) {
        nextAct = getLastResumeActivityFromStepStack(process.stepStack, dia);
        if (
          resumeLandingFromSettings &&
          dia.data &&
          dia.data[resumeLandingFromSettings]
        ) {
          nextAct = resumeLandingFromSettings;
          Logger.debug(
            "startProcessFromStoredId: using resumeLandingActivityId for terminal resume:",
            nextAct
          );
        }
      }

      const shouldStartFromBeginning =
        (!isTerminalEngagement && (!nextAct || nextAct === "")) ||
        (isTerminalEngagement && forceRestartFinishedEngagement);

      if (shouldStartFromBeginning) {
        nextAct = getStartActivity(dia);
        Logger.debug(
          "Start from beginning (empty next on non-terminal, or same-engagement + start-new on terminal):",
          nextAct
        );

        // Initialize userData with persistent data from last engagement if available
        let initialUserData: any = {};
        if (process.filledSelectedComponents?.selectedComponentsToCopy) {
          Logger.debug("Populating persistent data from last engagement:", process.filledSelectedComponents);
          const selectedComponents = process.filledSelectedComponents.selectedComponentsToCopy;

          // Extract values from the nested structure
          for (const [stepId, components] of Object.entries(selectedComponents)) {
            if (Array.isArray(components)) {
              for (const componentObj of components) {
                if (typeof componentObj === "object" && componentObj !== null) {
                  for (const [compId, value] of Object.entries(componentObj)) {
                    if (value !== null && value !== undefined) {
                      initialUserData[compId] = value;
                      Logger.debug(`Pre-filled component ${compId} with value:`, value);
                    }
                  }
                }
              }
            }
          }
        }

        setUserData(initialUserData);
        hasProgressedRef.current = false;
        setInitialStepStack([]);

        // Sync server when forcing a fresh run of a terminal engagement (author: same + start new)
        if (locProcessId) {
          const putBody: Record<string, unknown> = {
            status: "RUNNING",
            nextActivity: nextAct,
          };
          if (isTerminalEngagement && forceRestartFinishedEngagement) {
            putBody.stepStack = [];
            putBody.fullUserData = initialUserData;
          }
          await retryFetch(`${config.API_BASE}/api/processes/${locProcessId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(putBody),
          });
        }
      } else {
        // Resuming existing process - aggregate all userData from stepStack
        Logger.debug("===== RESUMING PROCESS =====");
        Logger.debug("Resuming at activity:", nextAct);
        Logger.debug("Backend stepStack LENGTH:", process.stepStack?.length ?? 0);
        Logger.debug("Backend stepStack FULL:", JSON.stringify(process.stepStack, null, 2));

        // Separate regular steps from temporary CURRENT_SCREEN markers
        let regularSteps: any[] = [];
        let currentScreenData: any = null;

        if (Array.isArray(process.stepStack)) {
          process.stepStack.forEach((step: any) => {
            if (step.activity?.startsWith("CURRENT_SCREEN|")) {
              // Extract the actual activity name and save the userData
              const actualActivity = step.activity.replace("CURRENT_SCREEN|", "");
              Logger.debug("Found CURRENT_SCREEN marker for activity:", actualActivity);
              Logger.debug("CURRENT_SCREEN userData:", JSON.stringify(step.userData));

              // Only use this if it matches the current nextActivity
              if (actualActivity === nextAct) {
                currentScreenData = step.userData ?? {};
              }
            } else {
              regularSteps.push(step);
            }
          });
        }

        // Start with fullUserData if available (preserves data even after going back)
        let aggregatedUserData:any = {};
        if (process.fullUserData && typeof process.fullUserData === 'object') {
          Logger.debug("=== Using fullUserData from backend (preserves all data) ===");
          Logger.debug("fullUserData keys:", Object.keys(process.fullUserData));
          Logger.debug("fullUserData full:", JSON.stringify(process.fullUserData));
          aggregatedUserData = { ...process.fullUserData };
        } else {
          Logger.debug("=== No fullUserData available, aggregating from stepStack ===");
          // Fallback: Aggregate userData from regular completed steps (old behavior)
          regularSteps.forEach((step: any, index: number) => {
            Logger.debug(`  Step [${index}]: activity="${step.activity}"`);
            Logger.debug(`  Step [${index}]: userData keys=[${Object.keys(step.userData ?? {}).join(", ")}]`);
            Logger.debug(`  Step [${index}]: userData full=${JSON.stringify(step.userData ?? {})}`);
            const stepUserData = step.userData ?? {};
            Object.entries(stepUserData).forEach(([key, value]) => {
              aggregatedUserData[key] = value;
            });
          });
        }

        // IMPORTANT: Merge in the current screen's data if we found a marker for it
        // This ensures the latest unsaved changes are preserved
        if (currentScreenData && Object.keys(currentScreenData).length > 0) {
          Logger.debug("=== Merging CURRENT_SCREEN userData ===");
          Logger.debug("CURRENT_SCREEN keys:", Object.keys(currentScreenData));
          Logger.debug("CURRENT_SCREEN full:", JSON.stringify(currentScreenData));
          Object.entries(currentScreenData).forEach(([key, value]) => {
            aggregatedUserData[key] = value;
          });
        }

        // ALSO merge in currentUserData if it exists (backward compatibility)
        if (process.currentUserData && typeof process.currentUserData === 'object') {
          Logger.debug("=== Merging currentUserData from backend (legacy) ===");
          Logger.debug("currentUserData keys:", Object.keys(process.currentUserData));
          Logger.debug("currentUserData full:", JSON.stringify(process.currentUserData));
          Object.entries(process.currentUserData).forEach(([key, value]) => {
            aggregatedUserData[key] = value;
          });
        }

        Logger.debug("=== Final aggregated userData from all sources ===");
        Logger.debug("Keys:", Object.keys(aggregatedUserData));
        Logger.debug("Full:", JSON.stringify(aggregatedUserData));
        setUserData(aggregatedUserData);

        // When resuming: if there are real steps in the stack (not just system markers), user has progressed
        const hasMeaningfulProgress = regularSteps.length > 0 &&
          !regularSteps.every((step: any) =>
            step.activity?.startsWith("TERMINATE") ||
            step.activity?.startsWith("REOPEN|") ||
            step.activity?.startsWith("CURRENT_SCREEN|")
          );
        hasProgressedRef.current = hasMeaningfulProgress;
        Logger.debug("Resuming process - has meaningful progress:", hasMeaningfulProgress);

        // IMPORTANT: Use only regular steps for initialStepStack (no CURRENT_SCREEN markers)
        // The stepStack from backend represents screens we've BEEN to and completed
        const resumeStepStack = [...regularSteps];
        Logger.debug("Setting initialStepStack LENGTH:", resumeStepStack.length);
        Logger.debug("Setting initialStepStack:", JSON.stringify(resumeStepStack, null, 2));
        Logger.debug("===== END RESUME =====");
        setInitialStepStack(resumeStepStack);
      }

      setInitialActivity(nextAct);
      setCurrentActivity(nextAct);
      setDiagram(dia);
      sessionStorage.removeItem("processID");
      sessionStorage.removeItem("nextActivity");
      } catch (error: any) {
        // Network errors or other exceptions while loading process
        Logger.error("Error in startProcessFromStoredId (runMode):", error);
        setLoadError({
          errorType: error?.message?.toLowerCase().includes("network") || error?.message?.toLowerCase().includes("fetch")
            ? "network_error"
            : "process_load_failed",
          errorMessage: error?.message || t("failed_to_load_engagement") || "Failed to load engagement data.",
          activityName: activityMetadata?.name,
        });
        return;
      }
    } else {
      // preview
      Logger.debug("previewMode detected");
      try {
        let response = await retryFetch(
          `${config.API_BASE}/api/prescriptions/${storedPrescriptionId}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setLoadError({
            errorType: response.status === 404 ? "prescription_not_found" : "general_error",
            errorMessage: errorData?.error || t("failed_to_load_activity") || "Failed to load activity.",
            activityName: activityMetadata?.name,
          });
          return;
        }

        let pres = await response.json();

        let dia = convertPrescription(pres.diagramJSON);
        // Add author information to diagram for proper author detection
        dia.fiUser = pres.fiUser;
        let startAct = getStartActivity(dia);
        setInitialActivity(startAct);
        setCurrentActivity(startAct);
        setInitialStepStack([]);
        setDiagram(dia);
      } catch (error: any) {
        // Network errors or other exceptions while loading prescription
        Logger.error("Error in startProcessFromStoredId (previewMode):", error);
        setLoadError({
          errorType: error?.message?.toLowerCase().includes("network") || error?.message?.toLowerCase().includes("fetch")
            ? "network_error"
            : "prescription_not_found",
          errorMessage: error?.message || t("failed_to_load_activity") || "Failed to load activity.",
          activityName: activityMetadata?.name,
        });
        return;
      }
    }
  }

  const widForSettings =
    activityMetadata?.prescriptionId ||
    searchParams.get("wid") ||
    (typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("prescriptionID")
      : null);

  useEffect(() => {
    if (!widForSettings) {
      setResumeExitSettings({ softExit: false, landingActivityId: "" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await retryFetch(
          `${config.API_BASE}/api/prescriptions/${widForSettings}/settings`,
          { credentials: "include" }
        );
        if (!res.ok || cancelled) return;
        const s = await res.json();
        const land =
          typeof s.resumeLandingActivityId === "string"
            ? s.resumeLandingActivityId
            : "";
        const softExit =
          s.engagementMode === "same" &&
          s.resumeFinished === true &&
          land.length > 0;
        if (!cancelled) {
          setResumeExitSettings({ softExit, landingActivityId: land });
        }
      } catch {
        if (!cancelled) {
          setResumeExitSettings({ softExit: false, landingActivityId: "" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [widForSettings]);

  async function tryStartOrResumeBySameEngagementSettings(
    prescriptionId: string
  ) {
    try {
      const settingsRes = await retryFetch(
        `${config.API_BASE}/api/prescriptions/${prescriptionId}/settings`,
        { credentials: "include" }
      );
      if (!settingsRes.ok) {
        await startProcess(prescriptionId);
        return;
      }
      const settings = await settingsRes.json();
      if (
        settings.engagementMode !== "same" ||
        settings.resumeFinished !== true
      ) {
        await startProcess(prescriptionId);
        return;
      }
      const searchRes = await retryFetch(
        `${config.API_BASE}/api/user/process/search?workflowId=${encodeURIComponent(
          prescriptionId
        )}&limit=1&allStatuses=true`,
        { credentials: "include" }
      );
      if (!searchRes.ok) {
        await startProcess(prescriptionId);
        return;
      }
      const data = await searchRes.json();
      const latest = data?.list?.[0];
      if (latest?._id) {
        await startProcessFromStoredId(prescriptionId, latest._id);
      } else {
        await startProcess(prescriptionId);
      }
    } catch (error: any) {
      Logger.error("tryStartOrResumeBySameEngagementSettings:", error);
      await startProcess(prescriptionId);
    }
  }

  async function startPreview() {}

  useEffectOnce(() => {
    const showPreview = async () => {
      // let queryParams = getQueryParams();
      const deeplinkId = searchParams.get("id");

      const queryPrescriptionId = searchParams.get("wid");
      const queryProcessId = searchParams.get("pid");

      const storedDraftPrescriptionId =
        sessionStorage.getItem("draftprescription");

      const storedPrescriptionId = sessionStorage.getItem("prescriptionID");
      const storedProcessId = sessionStorage.getItem("processID");
      const storedRunProcessId = sessionStorage.getItem("runProcessID");

      // Check if user is authenticated (reliable check via localStorage)
      let isAuthenticated = false;
      try {
        const authData = localStorage.getItem("auth");
        isAuthenticated = !!(authData && JSON.parse(authData)?.email);
      } catch (e) {
        isAuthenticated = false;
      }

      if (deeplinkId) {
        // retrieve prescription from deeplink API
        let response = await fetch(
          `${config.API_BASE}/api/deeplinks/${deeplinkId}`
        );
        let presData = await response.json();

        Logger.debug(
          `runMode instantiated for shared link ${presData.prescriptionId}`
        );
        await startProcess(presData.prescriptionId);
      } else if (queryPrescriptionId) {
        if (queryProcessId && isAuthenticated) {
          Logger.debug(
            `Resuming process id ${JSON.stringify(queryProcessId)} found in URL`
          );
          await startProcessFromStoredId(queryPrescriptionId, queryProcessId);
          // Clear sessionStorage after successfully resuming from URL parameters
          sessionStorage.removeItem("prescriptionID");
          sessionStorage.removeItem("processID");
          sessionStorage.removeItem("runProcessID");
        } else if (queryProcessId && !isAuthenticated) {
          // User is not authenticated but has a processId in URL (e.g. page refresh on public activity)
          // Cannot resume the process without auth, so strip pid and start fresh
          Logger.debug(
            `Not authenticated — ignoring pid ${queryProcessId}, starting fresh process for wid ${queryPrescriptionId}`
          );
          // Remove pid from URL to avoid the same issue on next refresh
          const browserSearchParams = new URLSearchParams(window.location.search);
          browserSearchParams.delete("pid");
          window.history.replaceState({}, "", "?" + browserSearchParams.toString());

          await startProcess(queryPrescriptionId);
        }
        else if (storedDraftPrescriptionId) {
          // retrieve prescription directly
          await startProcessFromCache(storedDraftPrescriptionId);
        } else {
          Logger.debug(
            `Starting new process for workflow id ${JSON.stringify(
              queryPrescriptionId
            )} found in URL`
          );
          if (isAuthenticated) {
            await tryStartOrResumeBySameEngagementSettings(queryPrescriptionId);
          } else {
            await startProcess(queryPrescriptionId);
          }
        }
      } else if (storedPrescriptionId) {
        // ONLY use sessionStorage if there's NO wid in URL
        // This handles the case where user was working on an activity, then navigates away and back
        Logger.debug(
            `No URL params but found prescriptionID in sessionStorage: wid ${storedPrescriptionId} pid ${storedProcessId} runpid ${storedRunProcessId}`
        );
        const locProcessId = storedProcessId ?? storedRunProcessId;

        // If not authenticated, don't try to resume a process — start fresh
        if (locProcessId && !isAuthenticated) {
          Logger.debug(
            `Not authenticated — ignoring stored processId ${locProcessId}, starting fresh process for wid ${storedPrescriptionId}`
          );
          sessionStorage.removeItem("processID");
          sessionStorage.removeItem("runProcessID");
          await startProcess(storedPrescriptionId);
        } else {
          Logger.debug(
              `Using sessionStorage locProcessId ${JSON.stringify(
                  locProcessId
              )}`
          );
          await startProcessFromStoredId(storedPrescriptionId, locProcessId);
        }
      } else {
        // No prescription ID found - show error
        setLoadError({
          errorType: "prescription_not_found",
          errorMessage: t("diagram_not_found") || "Activity not found. Please make sure you have the correct link.",
        });
      }
    };
    showPreview();

    return () => {
      sessionStorage.removeItem("isWelcome");
      Logger.warn(
        "PREVIEW IS UNMOUNTED!!!!!!!!!!!!!!!!!!--------------------!!!!!!!!!!!!!!!!!!!!-!_!!!!!----!!!!"
      );
    };
  }, []);

  const onDone = async (
    optionId: any,
    userDataFromDialog: any,
    newStepStack: any
  ) => {
    const effectiveUserData = userDataFromDialog ?? userData;

    Logger.debug("DONE Selected option: ", optionId);
    Logger.debug("DONE User data:");
    Logger.debug(effectiveUserData);

    // User has completed the activity - mark as having made changes
    hasProgressedRef.current = true;
    // Mark process as finished to prevent auto-save from overwriting FINISHED status
    processFinishedRef.current = true;

    if (processId) {
      try {
        // create log entry
        Logger.debug("PROCESSLOG #1", processId, currentActivity, optionId);
        await retryFetch(`${config.API_BASE}/api/processlog/${processId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            activity: currentActivity,
            selectedOptionId: optionId,
            userData: JSON.stringify(effectiveUserData),
            message: "User action",
          }),
        });

        // update process and set to FINISHED
        const finishResponse = await retryFetch(`${config.API_BASE}/api/processes/${processId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            status: "FINISHED",
            nextActivity: "",
            stepStack: newStepStack,
            fullUserData: effectiveUserData,
          }),
        });

        if (!finishResponse.ok) {
          Logger.debug("[onDone] Failed to mark process as FINISHED, retrying...", finishResponse.status);
          // Retry once
          const retryResponse = await retryFetch(`${config.API_BASE}/api/processes/${processId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              status: "FINISHED",
              nextActivity: "",
              stepStack: newStepStack,
              fullUserData: effectiveUserData,
            }),
          });
          if (!retryResponse.ok) {
            console.error("[onDone] Failed to mark process as FINISHED after retry:", retryResponse.status);
            toast.error("Failed to mark engagement as finished. Please try again.");
            return;
          }
        }

        trackAppEvent("engagement_completed", {
          processId,
          prescriptionId: activityMetadata?.prescriptionId || diagram?.prescriptionId,
          activityName: activityMetadata?.name,
        });

        // => zurück zum alten Prozess, diagram laden, stepStack löschen, initialActivity anhand der Auswahl von hier neu setzen

        let response = await retryFetch(
          `${config.API_BASE}/api/processes/${processId}`
        );
        if (!response.ok) {
          console.error("[onDone] Failed to fetch process after marking FINISHED:", response.status);
          // Process is FINISHED on backend, safe to show completion
          return navigateFromPreview();
        }
        let processData = await response.json();

        let parentProcessId = processData.parentProcessId;

        if (!parentProcessId) {
          return navigateFromPreview();
        }

        while (parentProcessId) {
          let exitNextActivities = processData.exitNextActivities;

          // get diagram from parent!
          response = await retryFetch(
            `${config.API_BASE}/api/processes/${parentProcessId}`
          );
          if (!response.ok) {
            console.error("[onDone] Failed to fetch parent process:", parentProcessId);
            return navigateFromPreview();
          }
          let parentProcessData = await response.json();

          let parentDia = convertPrescription(
            parentProcessData.fiPrescriptionHistory.diagramJSON
          );
          // Add author information to diagram for proper author detection
          parentDia.fiUser = parentProcessData.fiPrescriptionHistory.fiUser || parentProcessData.fiUser;
          parentDia = await applyLivePrescriptionDiagramMerge(
            parentDia,
            parentProcessData.fiPrescription,
            retryFetch,
          );

          Logger.debug("Stored exitOptions", exitNextActivities);
          let nextActivities = exitNextActivities.find(
            (option: any) => option.value === optionId
          )?.nextActivities;
          Logger.debug(
            "Those are the next activities from the selected option",
            nextActivities
          );
          if (nextActivities?.length) {
            parentProcessData.stepStack.push({
              activity: "REOPEN|" + processId,
              userData: effectiveUserData,
            });

            // update process
            const parentUpdateResponse = await retryFetch(
              `${config.API_BASE}/api/processes/${parentProcessId}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                  status: "RUNNING",
                  nextActivity: nextActivities[0],
                  stepStack: parentProcessData.stepStack,
                  pendingInnerProcess: "",
                }),
              }
            );

            if (!parentUpdateResponse.ok) {
              console.error("[onDone] Failed to update parent process to RUNNING:", parentProcessId);
            }

            setProcessId(parentProcessId);
            updateSearchParams({ pid: parentProcessId });
            console.log(
              `useEffectOnce set pid parentProcessId ${parentProcessId}`
            );

            setDiagram(parentDia);
            setInitialActivity(nextActivities[0]);
            setInitialStepStack(parentProcessData.stepStack);
            setCurrentActivity(nextActivities[0]);
            sessionStorage.removeItem("processID");
            sessionStorage.removeItem("nextActivity");

            parentProcessId = "";
          } else {
            // FINISH outer process, because there is no continuation!
            const outerFinishResponse = await retryFetch(
              `${config.API_BASE}/api/processes/${parentProcessId}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                  status: "FINISHED",
                  nextActivity: "",
                  stepStack: [],
                }),
              }
            );

            if (!outerFinishResponse.ok) {
              console.error("[onDone] Failed to mark outer process as FINISHED:", parentProcessId);
              // Retry once for outer process too
              await retryFetch(
                `${config.API_BASE}/api/processes/${parentProcessId}`,
                {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    status: "FINISHED",
                    nextActivity: "",
                    stepStack: [],
                  }),
                }
              );
            }

            // check if the outer process was nested
            response = await retryFetch(
              `${config.API_BASE}/api/processes/${parentProcessId}`
            );
            if (!response.ok) {
              console.error("[onDone] Failed to fetch outer process after FINISHED:", parentProcessId);
              return navigateFromPreview();
            }
            processData = await response.json();

            parentProcessId = processData.parentProcessId;

            if (!parentProcessId) {
              return navigateFromPreview();
            }
          }
        }
      } catch (error) {
        console.error("[onDone] Error during completion flow:", error);
        toast.error("Something went wrong finishing the engagement. Please try again.");
      }
    } else {
      navigateFromPreview();
    }
  };

  const onSoftExit = async (
    optionId: any,
    userDataFromDialog: any,
    newStepStack: any
  ) => {
    const effectiveUserData = userDataFromDialog ?? userData;
    const landingId = resumeExitSettings.landingActivityId;
    const landingValid =
      !!landingId &&
      diagram?.data &&
      typeof diagram.data === "object" &&
      !!diagram.data[landingId];

    if (!processId || !resumeExitSettings.softExit || !landingValid) {
      await onDone(optionId, userDataFromDialog, newStepStack);
      return;
    }

    try {
      const procRes = await retryFetch(
        `${config.API_BASE}/api/processes/${processId}`
      );
      if (procRes.ok) {
        const p = await procRes.json();
        if (p.parentProcessId) {
          await onDone(optionId, userDataFromDialog, newStepStack);
          return;
        }
      }
    } catch {
      await onDone(optionId, userDataFromDialog, newStepStack);
      return;
    }

    hasProgressedRef.current = true;

    try {
      await retryFetch(`${config.API_BASE}/api/processlog/${processId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          activity: currentActivity,
          selectedOptionId: optionId,
          userData: JSON.stringify(effectiveUserData),
          message: "Soft exit — engagement continues",
        }),
      });

      const putRes = await retryFetch(
        `${config.API_BASE}/api/processes/${processId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            status: "RUNNING",
            nextActivity: landingId,
            stepStack: newStepStack,
            fullUserData: effectiveUserData,
          }),
        }
      );

      if (!putRes.ok) {
        toast.error("Could not save your progress. Please try again.");
        return;
      }

      trackAppEvent("engagement_soft_exit", {
        processId,
        prescriptionId:
          activityMetadata?.prescriptionId || diagram?.prescriptionId,
        activityName: activityMetadata?.name,
      });

      navigate("/portal");
    } catch (error) {
      console.error("[onSoftExit]", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  // Check if user has made any meaningful progress in the activity
  // Simple approach: if they haven't clicked "Next" to move to another activity, no meaningful changes
  // Helper function to get field keys that belong to a specific activity
  const getActivityFieldKeys = (diagram: any, activityId: string): Set<string> => {
    const fieldKeys = new Set<string>();

    if (!diagram?.data?.[activityId]?.custom || !Array.isArray(diagram.data[activityId].custom)) {
      Logger.warn(`getActivityFieldKeys - activity ${activityId} has no custom components`);
      return fieldKeys;
    }

    for (const component of diagram.data[activityId].custom) {
      if (component?.content?.key) {
        fieldKeys.add(component.content.key);
      }
      // aiprompt and richtext_dynamic store data using component.id, not content.key
      if ((component?.type === 'aiprompt' || component?.type === 'richtext_dynamic') && component?.id) {
        fieldKeys.add(component.id);
      }
    }

    Logger.debug(`getActivityFieldKeys - activity ${activityId} has ${fieldKeys.size} fields: [${Array.from(fieldKeys).join(', ')}]`);
    return fieldKeys;
  };

  // Helper function to filter userData to only include fields for a specific activity
  const filterUserDataForActivity = (userData: any, diagram: any, activityId: string): any => {
    const activityFieldKeys = getActivityFieldKeys(diagram, activityId);
    const filtered: any = {};

    for (const [key, value] of Object.entries(userData)) {
      if (activityFieldKeys.has(key)) {
        filtered[key] = value;
      }
    }

    Logger.debug(`filterUserDataForActivity - filtered ${Object.keys(filtered).length} fields out of ${Object.keys(userData).length} total fields for activity ${activityId}`);
    return filtered;
  };

  // Check if user has made any changes to the activity
  const hasUserMadeChanges = () => {
    return hasProgressedRef.current;
  };

  // Auto-save current userData by updating the last stepStack entry
  // If we're on a screen that's already in the stepStack (going back), update its userData
  // Otherwise, the userData will be saved when user clicks "Next"
  const saveCurrentProgress = async () => {
    if (!processId || !currentActivity || !userData) {
      return;
    }

    // Don't overwrite FINISHED status with RUNNING after process completion
    if (processFinishedRef.current) {
      Logger.debug("saveCurrentProgress - process already finished, skipping auto-save");
      return;
    }

    // Check if userData has actually changed
    const currentUserDataStr = JSON.stringify(userData);
    if (currentUserDataStr === lastSavedUserDataRef.current) {
      Logger.debug("saveCurrentProgress - userData unchanged, skipping save");
      return;
    }

    Logger.debug("===== AUTO-SAVE with userData update =====");
    Logger.debug("saveCurrentProgress - currentActivity:", currentActivity);
    Logger.debug("saveCurrentProgress - userData keys:", Object.keys(userData));

    // Don't save if the diagram for this activity isn't loaded yet - filtering would
    // wipe out all fields and overwrite previously saved data with an empty object.
    const activityFieldKeysCheck = getActivityFieldKeys(diagram, currentActivity);
    if (activityFieldKeysCheck.size === 0) {
      Logger.debug("saveCurrentProgress - no known fields for activity yet, skipping save to avoid data loss");
      return;
    }

    // Snapshot what we're about to save so we can detect if it became stale
    // (user navigated away / edited again) while the GET below was in flight.
    const activityAtSaveStart = currentActivity;
    const userDataAtSaveStart = userData;

    try {
      // Get the current process
      const response = await retryFetch(
        `${config.API_BASE}/api/processes/${processId}`
      );
      const process = await response.json();

      // Abort if the user navigated to another activity or edited again while
      // this GET was in flight - a fresher save will (or already did) handle it.
      if (activityAtSaveStart !== localCurrentActivityRef.current || userDataAtSaveStart !== userDataRef.current) {
        Logger.debug("saveCurrentProgress - state changed during fetch, aborting stale save");
        return;
      }

      Logger.debug("saveCurrentProgress - backend stepStack:", JSON.stringify(process.stepStack, null, 2));

      // Check if we're on a screen that's in the stepStack (navigated back)
      // If so, update its userData. Otherwise, stepStack represents completed screens.
      let updatedStepStack = [...(process.stepStack ?? [])];

      // Remove any existing temporary "CURRENT_SCREEN" marker from previous auto-saves
      updatedStepStack = updatedStepStack.filter((step: any) =>
        !step.activity?.startsWith("CURRENT_SCREEN|")
      );

      // Find the last occurrence of currentActivity in stepStack (searching backwards)
      let lastStepIndex = -1;
      for (let i = updatedStepStack.length - 1; i >= 0; i--) {
        if (updatedStepStack[i].activity === currentActivity) {
          lastStepIndex = i;
          break;
        }
      }

      // IMPORTANT: Filter userData to only include fields belonging to this activity
      // Each stepStack entry should only contain fields defined in its activity's custom components
      const activitySpecificUserData = filterUserDataForActivity(userData, diagram, currentActivity);
      Logger.debug(`saveCurrentProgress - filtered userData for activity ${currentActivity}:`, activitySpecificUserData);

      if (lastStepIndex >= 0) {
        // We're on a screen we've been to before (went back to it)
        // Update its userData with current changes (only this activity's fields)
        Logger.debug(`saveCurrentProgress - updating stepStack[${lastStepIndex}] userData for activity ${currentActivity}`);
        updatedStepStack[lastStepIndex] = {
          ...updatedStepStack[lastStepIndex],
          userData: activitySpecificUserData,
        };
      } else {
        // We're on a new screen that hasn't been added to stepStack yet
        // Add a temporary marker entry so we can restore this data on resume
        Logger.debug("saveCurrentProgress - adding temporary CURRENT_SCREEN marker to stepStack");
        updatedStepStack.push({
          activity: `CURRENT_SCREEN|${currentActivity}`,
          userData: activitySpecificUserData,
        });
      }

      await retryFetch(`${config.API_BASE}/api/processes/${processId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          status: "RUNNING",
          nextActivity: currentActivity,
          stepStack: updatedStepStack,
          fullUserData: userData, // Save complete userData to preserve data across back/forward navigation
        }),
      });

      lastSavedUserDataRef.current = currentUserDataStr;
      Logger.debug("===== AUTO-SAVE SUCCESS =====");
    } catch (error) {
      Logger.error("saveCurrentProgress - failed to save:", error);
    }
  };

  const onPause = async () => {
    navigateFromPreview();
  };
  const onNextActivity = async (
    optionId: any,
    nextActivity: any,
    stepStack: any,
    userDataOverride?: any
  ) => {
    // RenderDialog sometimes calls setUserData(X) immediately before calling this
    // function. Since setState is async, `userData` here would still be the stale
    // pre-update value. Callers that just changed userData must pass it explicitly.
    const effectiveUserData = userDataOverride ?? userData;

    Logger.debug("Selected option: ", optionId);
    Logger.debug("User data:");
    Logger.debug(effectiveUserData);
    Logger.debug("Next node will be: ", nextActivity);

    // User has progressed to another activity - mark as having made changes
    hasProgressedRef.current = true;

    if (processId) {
      // create log entry
      Logger.debug(
        "------------- onNextActivity BEFORE process log --------------"
      );
      Logger.debug("PROCESSLOG #3", processId, currentActivity, optionId);
      await retryFetch(`${config.API_BASE}/api/processlog/${processId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          activity: currentActivity,
          selectedOptionId: optionId,
          userData: JSON.stringify(effectiveUserData),
          message: "User action",
        }),
      });

      Logger.debug(
        "------------- onNextActivity BEFORE data item mapping --------------",
        effectiveUserData
      );
      // consider data item mapping?
      let newUserData: any = structuredClone(effectiveUserData);
      if (diagram.data[nextActivity].dataitemmapping) {
        for (let mapping of diagram.data[nextActivity].dataitemmapping) {
          // mapping is {key, description, mappedToOuter:{key, description}}
          if (mapping?.mappedToOuter?.key) {
            newUserData[mapping.key] = newUserData[mapping.mappedToOuter.key];
          }
        }
        setUserData(newUserData);

        stepStack[stepStack.length - 1].userData = newUserData;
        Logger.debug("AFTER ADDING DATA ITEM MAPPING", newUserData);
      }

      Logger.debug(
        "------------- onNextActivity BEFORE process update --------------",
        effectiveUserData,
        newUserData
      );
      // update process
      await retryFetch(`${config.API_BASE}/api/processes/${processId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          status: "RUNNING",
          nextActivity,
          stepStack,
          fullUserData: newUserData, // Save complete userData to preserve data across back/forward navigation
        }),
      });
      setCurrentActivity(nextActivity);
    }
  };

  const onNestedActivity = async (
    presHistoryId: any,
    optionsWithHandles: any,
    outerActivityId: any
  ) => {
    Logger.debug(
      "PREVIEW received switching to nested activity with",
      presHistoryId,
      optionsWithHandles,
      outerActivityId
    );
    if (processId) {
      // determine nextActivities from optionHandles
      let exitOptions = [];
      for (let opt of optionsWithHandles) {
        let nextActivities = [];
        let handle = opt.handle;
        for (let edge of diagram.edges) {
          if (edge.sourceHandle === handle) {
            nextActivities.push(edge.target);
          }
        }
        exitOptions.push({
          value: opt.value,
          nextActivities,
        });
      }

      Logger.debug("Determined next activities", exitOptions);

      Logger.debug(
        "------------- onNestedActivity BEFORE creating new nested process --------------",
        userData
      );
      // -> Create a new process (with parent process!)
      let response = await retryFetch(
        `${config.API_BASE}/api/processes/${presHistoryId}/${processId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: auth.email,
            exitNextActivities: exitOptions,
            outerActivityId,
          }),
        }
      );

      let process = await response.json();

      let pendingInnerProcess = process.processId;

      Logger.debug(
        "------------- onNestedActivity BEFORE setting outer process to pending --------------"
      );
      // set to pending
      response = await retryFetch(
        `${config.API_BASE}/api/processes/setpending/${processId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            pendingInnerProcess,
          }),
        }
      );

      setProcessId(pendingInnerProcess);
      updateSearchParams({ pid: pendingInnerProcess });
      console.log(
        `useEffectOnce set pid pendingInnerProcess ${pendingInnerProcess}`
      );

      // -> set diagram, initial activity and initial step stack (leer?) for RenderDialog
      let dia = convertPrescription(process.diagramJSON);
      // Add author information to diagram for proper author detection
      dia.fiUser = process.fiUser;
      const nextAct = getStartActivity(dia);

      Logger.debug(
        "------------- onNestedActivity BEFORE setting start activity and TERMINATE step stack entry --------------",
        userData
      );
      // update new process with start activity
      await retryFetch(
        `${config.API_BASE}/api/processes/${pendingInnerProcess}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            status: "RUNNING",
            nextActivity: nextAct,
            stepStack: [{ activity: "TERMINATE", userData: userData }],
          }),
        }
      );

      setInitialActivity(nextAct);
      setInitialStepStack([{ activity: "TERMINATE", userData: userData }]);
      setCurrentActivity(nextAct);
      setDiagram(dia);

      sessionStorage.removeItem("processID");
      sessionStorage.removeItem("nextActivity");
    } else {
    }
  };

  const onBackFromNested = async () => {
    Logger.debug(
      "Coming back from nested activity => TERMINATE inner activity and continue on previous step from outer activity"
    );
    // get process information from outer process (processId)
    // step stack to pop latest activity from there, diagram from parentDia
    // terminate inner process (parentProcessId)

    let response = await retryFetch(
      `${config.API_BASE}/api/processes/${processId}`
    );
    let innerProcessData = await response.json();
    let parentProcessId = innerProcessData.parentProcessId;

    // create log entry for inner process
    Logger.debug("PROCESSLOG #4", processId, currentActivity, "GOINGBACK");
    await retryFetch(`${config.API_BASE}/api/processlog/${processId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        activity: currentActivity,
        selectedOptionId: "GOINGBACK",
        userData: JSON.stringify(userData),
        message: "User action",
      }),
    });

    response = await retryFetch(
      `${config.API_BASE}/api/processes/${parentProcessId}`
    );
    let processData = await response.json();

    let oldStepStack = processData.stepStack;

    let nextStepFromOuter = oldStepStack.pop();

    let theActivity =
      typeof nextStepFromOuter === "string"
        ? nextStepFromOuter
        : nextStepFromOuter.activity;

    Logger.debug(
      "After POPPING last step from step stack",
      nextStepFromOuter,
      oldStepStack
    );

    setUserData(nextStepFromOuter.userData ? nextStepFromOuter.userData : {});

    response = await retryFetch(
      `${config.API_BASE}/api/processes/${processId}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );

    // update outer process
    await retryFetch(`${config.API_BASE}/api/processes/${parentProcessId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        status: "RUNNING",
        nextActivity: theActivity,
        stepStack: oldStepStack,
        pendingInnerProcess: "",
      }),
    });

    setProcessId(parentProcessId);
    updateSearchParams({ pid: parentProcessId });
    console.log(`onBackFromNested set pid parentProcessId ${parentProcessId}`);
    let dia = convertPrescription(processData.fiPrescriptionHistory.diagramJSON);
    // Add author information to diagram for proper author detection
    dia.fiUser = processData.fiPrescriptionHistory.fiUser || processData.fiUser;
    if (!dia.prescriptionLanguage && (processData.fiPrescriptionHistory as any)?.prescriptionLanguage) {
      dia.prescriptionLanguage = (processData.fiPrescriptionHistory as any).prescriptionLanguage;
    }
    dia = await applyLivePrescriptionDiagramMerge(
      dia,
      processData.fiPrescription,
      retryFetch,
    );
    setDiagram(dia);
    setInitialActivity(theActivity);
    setInitialStepStack(oldStepStack);
    setCurrentActivity(theActivity);
    sessionStorage.removeItem("processID");
    sessionStorage.removeItem("nextActivity");
  };

  const onBackToFinishedNested = async (
    nestedProcessId: any,
    stepStack: any
  ) => {
    // current process is the outer one
    // stepStack is already popped (REOPEN step removed) and must be written to this process
    // pendingInnerProcess must be set again to nestedProcessId
    // nextActivity must be set to outerActivityId of nested process
    // Retrieve nested process information and get nextActivity popped from its step stack (write step stack back?)
    // nested process must be RUNNING again, log entry to change activity and to running

    let response = await retryFetch(
      `${config.API_BASE}/api/processes/${nestedProcessId}`
    );
    let nestedProcessData = await response.json();

    let nextNestedActivity = nestedProcessData.stepStack.pop();

    let theActivity =
      typeof nextNestedActivity === "string"
        ? nextNestedActivity
        : nextNestedActivity.activity;

    Logger.debug(
      "Outer process needs this new information: pendingInnerProcess, stepStack, nextActivity",
      nestedProcessId,
      stepStack,
      nestedProcessData.outerActivityId
    );

    // update outer process
    await retryFetch(
      `${config.API_BASE}/api/processes/updateouterfromreopen/${processId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          nextActivity: nestedProcessData.outerActivityId,
          stepStack,
          pendingInnerProcess: nestedProcessId,
        }),
      }
    );

    // log entry for outer process
    Logger.debug("PROCESSLOG #5", processId, currentActivity, "GOINGBACK");
    await retryFetch(`${config.API_BASE}/api/processlog/${processId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        activity: currentActivity,
        selectedOptionId: "GOINGBACK",
        userData: JSON.stringify(userData),
        message: "User action",
      }),
    });

    Logger.debug(
      "Nested process needs to be made RUNNING with nextActivity, stepStack",
      nextNestedActivity,
      nestedProcessData.stepStack
    );

    setUserData(nextNestedActivity.userData ? nextNestedActivity.userData : {});

    // update nested process and reopen
    await retryFetch(
      `${config.API_BASE}/api/processes/reopen/${nestedProcessId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          nextActivity: theActivity,
          stepStack: nestedProcessData.stepStack,
        }),
      }
    );

    // log entry for nested process
    Logger.debug(
      "PROCESSLOG #6",
      nestedProcessId,
      nextNestedActivity,
      "REOPENED"
    );
    await retryFetch(`${config.API_BASE}/api/processlog/${nestedProcessId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        activity: nextNestedActivity.activity,
        selectedOptionId: "REOPENED",
        userData: JSON.stringify(userData),
        message: "User action",
      }),
    });

    setProcessId(nestedProcessId);
    updateSearchParams({ pid: nestedProcessId });
    console.log(`onBackFromNested set pid nestedProcessId ${nestedProcessId}`);
    let dia = convertPrescription(nestedProcessData.fiPrescriptionHistory.diagramJSON);
    // Add author information to diagram for proper author detection
    dia.fiUser = nestedProcessData.fiPrescriptionHistory.fiUser || nestedProcessData.fiUser;
    if (!dia.prescriptionLanguage && (nestedProcessData.fiPrescriptionHistory as any)?.prescriptionLanguage) {
      dia.prescriptionLanguage = (nestedProcessData.fiPrescriptionHistory as any).prescriptionLanguage;
    }
    dia = await applyLivePrescriptionDiagramMerge(
      dia,
      nestedProcessData.fiPrescription,
      retryFetch,
    );
    setDiagram(dia);
    setInitialActivity(theActivity);
    setInitialStepStack(nestedProcessData.stepStack);
    setCurrentActivity(theActivity);
    sessionStorage.removeItem("processID");
    sessionStorage.removeItem("nextActivity");
  };

  const navigateFromPreview = () => {
    setIsActivityCompletedModalOpen(true);
  };

  useEffect(() => {
    if (!Object.keys(userData).length) {
      Logger.warn(
        "INITIAL USER DATA IS EMPTY !!!!!!!!!!!!!!---------------!!!!!!!!!-!_!__-!!!!!!!!!!!!!!"
      );
    }
  }, [userData]);

  // Expose saveCurrentProgress to parent via ref
  useEffect(() => {
    if (saveProgressRef) {
      saveProgressRef.current = saveCurrentProgress;
    }
  }, [saveProgressRef, userData, currentActivity, processId]);

  // Expose hasUserMadeChanges to parent via ref
  useEffect(() => {
    if (hasChangesRef) {
      hasChangesRef.current = hasUserMadeChanges;
    }
  }, [hasChangesRef]);

  // Debounced auto-save when userData changes
  useEffect(() => {
    userDataRef.current = userData;
    localCurrentActivityRef.current = currentActivity;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Only set up auto-save if we have meaningful data
    if (processId && currentActivity && Object.keys(userData).length > 0) {
      // Debounce: wait 2 seconds after last change before saving
      saveTimeoutRef.current = setTimeout(() => {
        Logger.debug("Auto-save triggered after userData change");
        saveCurrentProgress();
      }, 2000);
    }

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [userData, currentActivity, processId]);

  // Save on unmount (when actually leaving the page)
  useEffect(() => {
    return () => {
      Logger.debug("ActivityRunningPreview unmounting - saving current progress");
      // Cancel pending debounced save and save immediately
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Save immediately on unmount (synchronous as much as possible)
      if (processId && currentActivity && Object.keys(userData).length > 0) {
        saveCurrentProgress();
      }
    };
  }, []); // Empty deps - only runs on unmount

  // Handle retry by refreshing the page
  const handleRetry = () => {
    window.location.reload();
  };

  // Tagging dialog handlers for expert users
  const handleTaggingConfirm = useCallback(
    async (result: EngagementTaggingResult) => {
      setShowTaggingDialog(false);
      const prescriptionId = pendingStartProcessId.current;
      pendingStartProcessId.current = null;
      if (prescriptionId) {
        await startProcess(prescriptionId, result);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth.email],
  );

  // If access is denied, show the AccessDenied component
  if (accessDenied) {
    return (
      <AccessDenied
        reason={accessDenied.reason}
        activityName={accessDenied.activityName}
        onRetry={() => {
          // Clear access denied state and reload the page to re-attempt
          // loading with the new auth credentials
          setAccessDenied(null);
          window.location.reload();
        }}
      />
    );
  }

  // If there's a load error, show the ActivityLoadError component
  if (loadError) {
    return (
      <ActivityLoadError
        errorType={loadError.errorType}
        errorMessage={loadError.errorMessage}
        activityName={loadError.activityName}
        onRetry={handleRetry}
      />
    );
  }

  return (
    <>
      <div
        id="preview"
        className="relative flex-1 h-full min-h-0 flex flex-col overflow-hidden bg-gradient-to-br from-slate-50/80 via-white to-indigo-50/30 animate-in fade-in duration-300"
        dir={i18n.dir()}
      >
        <RenderDialog
          key={processId}
          diagram={diagram}
          initialActivity={initialActivity}
          initialStepStack={initialStepStack}
          userData={userData}
          setUserData={setUserData}
          onDone={onDone}
          onSoftExit={onSoftExit}
          softExitEnabled={
            !!(
              resumeExitSettings.softExit &&
              resumeExitSettings.landingActivityId &&
              diagram?.data?.[resumeExitSettings.landingActivityId]
            )
          }
          onPause={onPause}
          onQuotePane={onQuotePane}
          onNavigateToField={onNavigateToField}
          onAddTakeaway={onAddTakeaway}
          onNextActivity={onNextActivity}
          onNestedActivity={onNestedActivity}
          onBackFromNested={onBackFromNested}
          onBackToFinishedNested={onBackToFinishedNested}
          processId={processId}
          setDiagram={setDiagram}
          vendorMetadata={activityMetadata}
          onFollowExample={onFollowExample}
          onContentScroll={onContentScroll}
          mobileActionsSlot={mobileActionsSlot}
          additionalInfoRecordings={additionalInfoRecordings}
          autoPlayState={autoPlayState}
          onAutoPlayStateChange={setAutoPlayState as any}
        />
      </div>

      {/* Engagement tagging dialog — shown before creating a new process */}
      <EngagementTaggingDialog
        isOpen={showTaggingDialog}
        onConfirm={handleTaggingConfirm}
        onClose={() => {
          setShowTaggingDialog(false);
          pendingStartProcessId.current = null;
        }}
        activityName={activityMetadata?.name}
        confirmLabel={t("start_activity") || "Start Activity"}
        showEngagementNameField={true}
        showMentorOptions={isCurrentUserExpert}
      />
    </>
  );
};

export default ActivityRunningPreview;
